import { useState } from "react";
import { toast } from "sonner";
import { Nfc } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useReaderLine } from "../../hooks/useReaderLine";
import { useReaderBridge } from "../../hooks/useReaderBridge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { ReaderHelp } from "./ReaderHelp";
import { BridgePanel } from "./BridgePanel";

const norm = (u) => u.replace(/\s+/g, "").toUpperCase();
const pad = (n) => String(n).padStart(2, "0");

const LinkControls = ({ specimens, uid, onLinked }) => {
  const [target, setTarget] = useState("");
  const link = async () => {
    const s = specimens.find((x) => x.id === target);
    try {
      await api.put(`/admin/specimens/${s.id}`, {
        tag_uid: uid, name: s.name, spore_word: s.spore_word, sound_word: s.sound_word, meaning: s.meaning, icon: s.icon, voice: s.voice, slot: s.slot,
        memory: s.memory, sfx_prompt: s.sfx_prompt,
      });
      toast.success(`Tag linked to ${s.name}`);
      onLinked();
    } catch (err) {
      toast.error(errMsg(err, "Could not link tag"));
    }
  };
  return (
    <>
      <span data-testid="reader-test-unlinked" className="text-amber-300">Not linked to any object yet</span>
      <Select value={target} onValueChange={setTarget}>
        <SelectTrigger data-testid="reader-test-object-select" className="w-60 rounded-none border-slate-700 bg-slate-900">
          <SelectValue placeholder="Choose object to link" />
        </SelectTrigger>
        <SelectContent className="lab-dialog">
          {specimens.map((s) => (
            <SelectItem key={s.id} value={s.id} data-testid={`reader-test-option-${s.slot}`}>#{pad(s.slot)} {s.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <button data-testid="reader-test-link-button" disabled={!target} onClick={link} className="lab-btn-primary py-2">Link tag</button>
    </>
  );
};

export const ReaderTest = ({ specimens, onLinked, readOnly = false }) => {
  const [read, setRead] = useState(null);
  const onUid = (uid) => {
    const u = norm(uid);
    api.get("/reader/match", { params: { uid: u } })
      .then((r) => setRead({ uid: u, match: r.data.specimen }))
      .catch(() => setRead({ uid: u, match: null }));
  };
  const { inputProps, keys, lastKeyAt } = useReaderLine(onUid);
  const bridge = useReaderBridge(onUid);
  const match = read?.match || null;

  return (
    <section data-testid="reader-test" className="border border-cyan-400/30 bg-slate-950/70 p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="lab-label flex items-center gap-2 !text-cyan-300"><Nfc size={14} /> Reader check</p>
        <span data-testid="reader-test-key-count" className="font-mono text-xs tracking-widest text-slate-500">KEYS RECEIVED: {keys}</span>
      </div>
      <p className="mt-2 text-sm text-slate-400">Click the line below, then place any tagged object on the reader. Its ID should appear here within a second.</p>
      <label className="mt-3 flex items-center gap-3 border-b border-cyan-400/40 pb-2 font-mono text-cyan-300">
        <span className="text-lg">&gt;</span>
        <input
          data-testid="reader-test-input"
          {...inputProps}
          autoFocus
          autoComplete="off"
          spellCheck={false}
          placeholder="click here, then scan a tag"
          className="w-full bg-transparent text-lg tracking-[0.2em] text-cyan-200 outline-none placeholder:text-slate-600"
        />
      </label>
      {read && (
        <div data-testid="reader-test-result" className="mt-4 flex flex-wrap items-center gap-3 font-mono text-sm">
          <span className="text-slate-300">READ: <span data-testid="reader-test-uid" className="text-cyan-300">{read.uid}</span></span>
          {match ? (
            <span data-testid="reader-test-linked" className="text-emerald-300">Linked to #{pad(match.slot)} {match.name}</span>
          ) : readOnly ? (
            <span data-testid="reader-test-unlinked" className="text-amber-300">Not linked to any object (unseal to link it)</span>
          ) : (
            <LinkControls key={read.uid} specimens={specimens} uid={read.uid} onLinked={onLinked} />
          )}
        </div>
      )}
      <ReaderHelp keys={keys} lastKeyAt={lastKeyAt} bridgeOnline={bridge.online} />
      <BridgePanel online={bridge.online} lastPush={bridge.lastPush} />
    </section>
  );
};
